import { useEffect, useRef } from 'react';
import { writeHomeFeaturedCache } from '../services/homeFeaturedCache';
import { maestroRadioService } from '../services/maestroRadioService';

const GENRE_MAP = {
  jazz: { qobuzId: 80, label: 'Jazz' },
  classical: { qobuzId: 10, label: 'Classical' },
  rock: { qobuzId: 112, label: 'Rock' },
  blues: { qobuzId: 3, label: 'Blues' },
  folk: { qobuzId: 5, label: 'Folk' },
  alternative: { qobuzId: 112, label: 'Alternative' },
  pop: { qobuzId: 112, label: 'Pop' },
  rnb: { qobuzId: 127, label: 'R&B / Soul' },
  electronic: { qobuzId: 64, label: 'Electronic' },
  country: { qobuzId: 4, label: 'Country' },
  latin: { qobuzId: 149, label: 'Latin' },
  world: { qobuzId: 94, label: 'World' },
  ambient: { qobuzId: 64, label: 'Ambient' },
  acoustic: { qobuzId: 5, label: 'Acoustic' },
  progressive: { qobuzId: 112, label: 'Progressive' },
  avantgarde: { qobuzId: 80, label: 'Avant-garde' },
  hiphop: { qobuzId: 133, label: 'Hip-Hop' },
  metal: { qobuzId: 116, label: 'Metal' },
  soundtrack: { qobuzId: 91, label: 'Soundtracks' },
  soul: { qobuzId: 127, label: 'Soul / Funk' },
};

function shuffle(arr) {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

export function useHomeFeaturedRevalidate({
  pathname,
  userGenres,
  fetchFeaturedFn,
  fetchGenreAlbumsFn,
  fetchFeaturedPlaylistsFn,
  setPickedForYou,
  setNewReleases,
  setEditorPicks,
  setFeaturedPlaylists,
  setMyStations,
  setPopularGenre,
  setPopularGenreName,
}) {
  const lastFeaturedFetchAtRef = useRef(0);

  useEffect(() => {
    const onHome = pathname === '/app' || pathname === '/app/';
    if (!onHome) return;
    const now = Date.now();
    if (now - lastFeaturedFetchAtRef.current < 15_000) return;
    lastFeaturedFetchAtRef.current = now;

    const genrePromises = userGenres.map(async (gKey) => {
      const info = GENRE_MAP[gKey];
      if (!info) return null;
      const albums = await fetchGenreAlbumsFn(info.qobuzId, 30);
      return albums.length > 0 ? { genre: gKey, label: info.label, albums: shuffle(albums) } : null;
    });

    Promise.all(genrePromises).then((results) => {
      const next = results.filter(Boolean);
      setPickedForYou(next);
      writeHomeFeaturedCache({ pickedForYou: next });
    });

    fetchFeaturedFn('new-releases', 30).then((a) => {
      const next = shuffle(a);
      setNewReleases(next);
      writeHomeFeaturedCache({ newReleases: next });
    });
    fetchFeaturedFn('editor-picks', 30).then((a) => {
      const next = shuffle(a);
      setEditorPicks(next);
      writeHomeFeaturedCache({ editorPicks: next });
    });
    fetchFeaturedPlaylistsFn(12).then((items) => {
      setFeaturedPlaylists(items);
      writeHomeFeaturedCache({ featuredPlaylists: items });
    });

    maestroRadioService.getStations()
      .then((res) => {
        const stations = res.stations || [];
        setMyStations(stations);
        writeHomeFeaturedCache({ myStations: stations });
      })
      .catch(() => {
        setMyStations([]);
        writeHomeFeaturedCache({ myStations: [] });
      });

    const randomGenre = userGenres[Math.floor(Math.random() * userGenres.length)] || 'jazz';
    const gInfo = GENRE_MAP[randomGenre];
    if (gInfo) {
      setPopularGenreName(gInfo.label);
      fetchGenreAlbumsFn(gInfo.qobuzId, 30).then((a) => {
        const next = shuffle(a);
        setPopularGenre(next);
        writeHomeFeaturedCache({ popularGenre: next, popularGenreName: gInfo.label });
      });
    }
  }, [
    pathname,
    userGenres,
    fetchFeaturedFn,
    fetchGenreAlbumsFn,
    fetchFeaturedPlaylistsFn,
    setPickedForYou,
    setNewReleases,
    setEditorPicks,
    setFeaturedPlaylists,
    setMyStations,
    setPopularGenre,
    setPopularGenreName,
  ]);
}
